import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../app/store';
import { CreditBalanceState } from '../app/Slices/creditBalance';
import {
  isAddonProduct,
  getCreditsMonthly,
} from '../components/PreviewInvoice/helpers';

export interface CreditBalance {
  isLoading: boolean;
  totalCredits: number;
  usedCredits: number;
  remainingCredits: number;
  oneTimeCreditsPurchased: number;
  oneTimeCreditsUsed: number;
  oneTimeCreditsRemaining: number;
  usagePercentage: number;
  isRunningOutOfCredits: boolean;
  hasAdditionalCredits: boolean;

  lastRenewalDate: Date | null;
  nextRenewalDate: Date | null;

  warningElement: React.ReactNode | null;
}

export const useCreditBalance = (): CreditBalance => {
  const creditBalanceState: CreditBalanceState = useSelector(
    (state: RootState) => state.creditBalance
  );
  const { totalCredits, usedCredits } = creditBalanceState || {};

  const { products } = useSelector((state: RootState) => state.products);
  const [warningElement, setWarningElement] = useState<React.ReactNode>(null);

  const hasAdditionalCredits = useMemo(() => {
    if (!products?.length || !creditBalanceState) return false;
    const creditProduct = products.find((product) => isAddonProduct(product));
    if (!creditProduct) return false;
    const creditsMonthly = getCreditsMonthly(null, creditProduct);
    if (!creditsMonthly) return false;
    return totalCredits > creditsMonthly;
  }, [products, totalCredits]);

  useEffect(() => {
    if (creditBalanceState.error) {
      setWarningElement(<div>Failed to load credit balance</div>);
    } else if (creditBalanceState.warning) {
      setWarningElement(<div>{creditBalanceState.warning}</div>);
    }
  }, [creditBalanceState.warning, creditBalanceState.error]);

  const usagePercentage =
    totalCredits > 0 ? (usedCredits / totalCredits) * 100 : 0;
  // Consider one-time credits: user is not running out if they have bonus credits remaining
  const { remainingCredits } = creditBalanceState;
  const isRunningOutOfCredits =
    totalCredits > 0 && remainingCredits / totalCredits < 0.25;

  return {
    isLoading: creditBalanceState.loading,

    totalCredits: creditBalanceState.totalCredits, // amount of credits granted to the current team or its parent team
    usedCredits: creditBalanceState.usedCredits, // amount of credits used by the current team
    remainingCredits: creditBalanceState.remainingCredits, // amount of credits remaining for the current team or its parent team (includes one-time credits)
    oneTimeCreditsPurchased: creditBalanceState.oneTimeCreditsPurchased,
    oneTimeCreditsUsed: creditBalanceState.oneTimeCreditsUsed,
    oneTimeCreditsRemaining: creditBalanceState.oneTimeCreditsRemaining,

    hasAdditionalCredits,
    usagePercentage,
    isRunningOutOfCredits,

    lastRenewalDate: creditBalanceState.lastRenewalDate
      ? new Date(creditBalanceState.lastRenewalDate)
      : null,
    nextRenewalDate: creditBalanceState.nextRenewalDate
      ? new Date(creditBalanceState.nextRenewalDate)
      : null,
    warningElement: warningElement,
  };
};
